import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/home';

interface NavbarProps {
  currentLang: string;
  onLanguageChange: (lang: string) => void;
  solid?: boolean;
}

export default function Navbar({ currentLang, onLanguageChange, solid = false }: NavbarProps) {
  const { t } = useTranslation('home');
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  // On solid pages (non-home), always show solid bg regardless of scroll.
  // On home pages, transparent before scroll, solid after scroll.
  const isSolid = solid || scrolled;

  const navLinks = [
    { label: t('nav_home'), to: `/${currentLang}/` },
    { label: t('nav_spreadsheet'), to: `/${currentLang}/allchinabuy-spreadsheet/` },
    { label: t('nav_shopping_guide'), to: `/${currentLang}/guides/` },
    { label: t('nav_review'), to: `/${currentLang}/review/` },
    { label: t('nav_shipping'), to: `/${currentLang}/shipping-cost/` },
    { label: t('nav_safety'), to: `/${currentLang}/safety/` },
    { label: t('nav_blog'), to: `/${currentLang}/blog/` },
  ];

  const languages = [
    { code: 'en', label: t('lang_english') },
    { code: 'fr', label: t('lang_french') },
    { code: 'de', label: t('lang_german') },
    { code: 'da', label: t('lang_danish') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to={`/${currentLang}/`} className="flex items-center gap-2 shrink-0">
            <span
              className={`font-heading text-xl lg:text-2xl font-bold tracking-tight ${
                isSolid ? 'text-primary-700' : 'text-background-50'
              }`}
            >
              AllChinaBuy
            </span>
            <span
              className={`hidden sm:inline font-heading text-sm font-semibold ${
                isSolid ? 'text-primary-500' : 'text-background-50/80'
              }`}
            >
              Spreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold transition-colors whitespace-nowrap ${
                  isSolid
                    ? 'text-foreground-700 hover:text-primary-600'
                    : 'text-background-50 hover:text-background-50/80'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-semibold transition-colors whitespace-nowrap ${
                  isSolid
                    ? 'text-foreground-700 hover:text-primary-600'
                    : 'text-background-50 hover:text-background-50/80'
                }`}
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="dropdown-animate absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg max-h-80 overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    {t('dropdown_title')}
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-foreground-400 mr-2 text-xs"></i>
                      {t(cat.name)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side: Language + Mobile menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  isSolid
                    ? 'text-foreground-600 hover:bg-background-100 border border-background-200/60'
                    : 'text-background-50 hover:bg-background-50/10 border border-background-50/30'
                }`}
              >
                <i className="ri-global-line"></i>
                <span className="uppercase">{currentLang}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="dropdown-animate absolute top-full right-0 mt-2 w-40 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        onLanguageChange(lang.code);
                        setLangOpen(false);
                        setMobileOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        currentLang === lang.code
                          ? 'text-primary-700 bg-primary-50 font-medium'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded-md transition-colors ${
                isSolid
                  ? 'text-foreground-700 hover:bg-background-100'
                  : 'text-background-50 hover:bg-background-50/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {mobileOpen && (
          <div ref={mobileRef} className="lg:hidden border-t border-background-200/60 bg-background-50 shadow-lg max-h-[72vh] overflow-y-auto rounded-b-lg">
            <div className="px-3 py-4 space-y-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-3 mt-3 border-t border-background-200/60">
                <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                  {t('dropdown_title')}
                </p>
                <div className="grid grid-cols-2 gap-0.5 pt-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2 rounded-md text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      {t(cat.name)}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}